import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, Trash2, Edit3, X, AlertCircle, Phone, Calendar, CheckCircle2 } from 'lucide-react';
import DashboardShell from '@/components/layout/DashboardShell';
import { moduleBySlug } from '@/lib/modules';
import api from '@/lib/api';
import { formatApiError } from '@/lib/auth';

// Sub-entity registry for deep-built modules.
// Each entry: title, schema fields, optional row renderer.
const SUB_REGISTRY = {
  'school/classes': {
    title: 'Classes', singular: 'Class',
    schema: [
      { name: 'name', label: 'Class & arm', required: true, placeholder: 'JSS 2A' },
      { name: 'level', label: 'Level', type: 'select', options: ['Creche', 'Nursery', 'Primary', 'JSS', 'SS'] },
      { name: 'capacity', label: 'Capacity', type: 'number' },
      { name: 'teacher_name', label: 'Form teacher' },
    ],
    cols: ['name', 'level', 'capacity', 'teacher_name'],
  },
  'school/fee-schedules': {
    title: 'Fee Schedules', singular: 'Fee Schedule',
    schema: [
      { name: 'label', label: 'Schedule label', required: true, placeholder: 'Term 2 Tuition' },
      { name: 'class_arm', label: 'Apply to class & arm (blank = all)' },
      { name: 'amount', label: 'Amount (₦)', type: 'number', required: true },
      { name: 'frequency', label: 'Frequency', type: 'select', options: ['Once', 'Termly', 'Monthly', 'Annually'] },
      { name: 'due_date', label: 'Due date', type: 'date' },
    ],
    cols: ['label', 'class_arm', 'amount', 'frequency', 'due_date'],
    actions: [{ key: 'issue', label: 'Issue bills', endpoint: 'school/fee-schedules/{id}/issue', confirm: 'Generate fee bills for matching students?' }],
  },
  'school/fee-bills': {
    title: 'Fee Bills', singular: 'Fee Bill',
    schema: [
      { name: 'student_name', label: 'Student name', required: true },
      { name: 'class_arm', label: 'Class & arm' },
      { name: 'amount', label: 'Amount (₦)', type: 'number', required: true },
      { name: 'status', label: 'Status', type: 'select', options: ['issued', 'paid', 'partial', 'overdue', 'waived'] },
      { name: 'due_date', label: 'Due date', type: 'date' },
    ],
    cols: ['bill_no', 'student_name', 'class_arm', 'amount', 'status'],
  },
  'estate/levies': {
    title: 'Service Levies', singular: 'Levy',
    schema: [
      { name: 'unit_no', label: 'Unit / plot', required: true },
      { name: 'owner_name', label: 'Owner / tenant' },
      { name: 'category', label: 'Category', type: 'select', options: ['Service charge', 'Security', 'Maintenance', 'Refuse', 'Water', 'Other'] },
      { name: 'amount', label: 'Amount (₦)', type: 'number', required: true },
      { name: 'period', label: 'Period', placeholder: 'May 2026' },
      { name: 'status', label: 'Status', type: 'select', options: ['outstanding', 'paid', 'partial', 'waived'] },
    ],
    cols: ['ref', 'unit_no', 'category', 'amount', 'period', 'status'],
  },
  'estate/visitors': {
    title: 'Visitor Log', singular: 'Visitor',
    schema: [
      { name: 'visitor_name', label: 'Visitor name', required: true },
      { name: 'host_unit', label: 'Visiting unit', required: true },
      { name: 'phone', label: 'Phone' },
      { name: 'purpose', label: 'Purpose' },
      { name: 'vehicle_plate', label: 'Vehicle plate' },
    ],
    cols: ['visitor_name', 'host_unit', 'phone', 'purpose', 'vehicle_plate'],
  },
  'hospital/vitals': {
    title: 'Vitals Flowsheet', singular: 'Vitals',
    schema: [
      { name: 'patient_name', label: 'Patient', required: true },
      { name: 'temperature', label: 'Temperature (°C)', type: 'number' },
      { name: 'pulse', label: 'Pulse (bpm)', type: 'number' },
      { name: 'bp', label: 'Blood pressure', placeholder: '120/80' },
      { name: 'spo2', label: 'SpO₂ (%)', type: 'number' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
    cols: ['patient_name', 'temperature', 'pulse', 'bp', 'spo2'],
  },
  'hospital/opd-queue': {
    title: 'OPD Queue', singular: 'Queue Entry',
    schema: [
      { name: 'patient_name', label: 'Patient', required: true },
      { name: 'unit', label: 'Unit', type: 'select', options: ['OPD General', 'Cardiology', 'Pediatrics', 'OBGYN', 'Surgery'] },
      { name: 'priority', label: 'Priority', type: 'select', options: ['routine', 'urgent', 'emergency'] },
      { name: 'complaint', label: 'Chief complaint', type: 'textarea' },
    ],
    cols: ['queue_no', 'patient_name', 'unit', 'priority', 'status'],
  },
  'engineering/service-requests': {
    title: 'Service Requests', singular: 'Service Request',
    schema: [
      { name: 'client_name', label: 'Client name', required: true },
      { name: 'phone', label: 'Phone' },
      { name: 'category', label: 'Category', type: 'select', options: ['Computer repair', 'Electrical', 'Industrial', 'Solar/inverter', 'Installation', 'Other'] },
      { name: 'description', label: 'Description', type: 'textarea', required: true },
      { name: 'address', label: 'Address' },
      { name: 'estimate', label: 'Estimate (₦)', type: 'number' },
      { name: 'status', label: 'Status', type: 'select', options: ['submitted', 'estimated', 'approved', 'in-progress', 'completed', 'cancelled'] },
    ],
    cols: ['ref', 'client_name', 'category', 'estimate', 'status'],
  },
  'financials/accounts': {
    title: 'Chart of Accounts', singular: 'Account',
    schema: [
      { name: 'code', label: 'Account code', required: true, placeholder: '1000' },
      { name: 'name', label: 'Account name', required: true },
      { name: 'kind', label: 'Type', type: 'select', options: ['asset', 'liability', 'equity', 'revenue', 'expense'] },
      { name: 'parent_code', label: 'Parent (optional)' },
    ],
    cols: ['code', 'name', 'kind', 'parent_code'],
  },
  'financials/entries': {
    title: 'GL Entries', singular: 'Entry',
    schema: [
      { name: 'description', label: 'Description', required: true },
      { name: 'debit_account', label: 'Debit account' },
      { name: 'credit_account', label: 'Credit account' },
      { name: 'amount', label: 'Amount (₦)', type: 'number', required: true },
      { name: 'reference', label: 'Reference / source doc' },
    ],
    cols: ['description', 'debit_account', 'credit_account', 'amount', 'reference'],
  },
  'office/leave-requests': {
    title: 'Leave Requests', singular: 'Leave Request',
    schema: [
      { name: 'employee_name', label: 'Employee', required: true },
      { name: 'kind', label: 'Type', type: 'select', options: ['Annual', 'Sick', 'Casual', 'Maternity', 'Compassionate'] },
      { name: 'start_date', label: 'Start date', type: 'date' },
      { name: 'end_date', label: 'End date', type: 'date' },
      { name: 'reason', label: 'Reason', type: 'textarea' },
      { name: 'status', label: 'Status', type: 'select', options: ['pending', 'approved', 'rejected'] },
    ],
    cols: ['employee_name', 'kind', 'start_date', 'end_date', 'status'],
  },
  'office/tasks': {
    title: 'Tasks', singular: 'Task',
    schema: [
      { name: 'title', label: 'Title', required: true },
      { name: 'assignee', label: 'Assignee' },
      { name: 'priority', label: 'Priority', type: 'select', options: ['Low', 'Normal', 'High', 'Urgent'] },
      { name: 'due_date', label: 'Due date', type: 'date' },
      { name: 'status', label: 'Status', type: 'select', options: ['open', 'in-progress', 'blocked', 'done'] },
      { name: 'description', label: 'Description', type: 'textarea' },
    ],
    cols: ['title', 'assignee', 'priority', 'due_date', 'status'],
  },
  'restaurant/orders': {
    title: 'Orders', singular: 'Order',
    schema: [
      { name: 'customer_name', label: 'Customer / table', required: true },
      { name: 'kind', label: 'Order type', type: 'select', options: ['Dine-in', 'Takeaway', 'Delivery'] },
      { name: 'items', label: 'Items (free-text)', type: 'textarea' },
      { name: 'amount', label: 'Total (₦)', type: 'number' },
      { name: 'status', label: 'Status', type: 'select', options: ['received', 'preparing', 'ready', 'served', 'paid', 'cancelled'] },
    ],
    cols: ['order_no', 'customer_name', 'kind', 'amount', 'status'],
  },
  'retail/sales': {
    title: 'Sales', singular: 'Sale',
    schema: [
      { name: 'customer_name', label: 'Customer (optional)' },
      { name: 'item_summary', label: 'Items (summary)', required: true },
      { name: 'quantity', label: 'Total quantity', type: 'number' },
      { name: 'amount', label: 'Total (₦)', type: 'number', required: true },
      { name: 'tender', label: 'Tender', type: 'select', options: ['Cash', 'Transfer', 'Card', 'POS'] },
    ],
    cols: ['receipt_no', 'customer_name', 'item_summary', 'quantity', 'amount'],
  },
  'government/case-actions': {
    title: 'Case Actions', singular: 'Action',
    schema: [
      { name: 'case_no', label: 'Case number', required: true },
      { name: 'action', label: 'Action taken', required: true },
      { name: 'officer', label: 'Officer' },
      { name: 'outcome', label: 'Outcome', type: 'textarea' },
    ],
    cols: ['case_no', 'action', 'officer', 'outcome'],
  },
  'church-ngo/donations': {
    title: 'Donations', singular: 'Donation',
    schema: [
      { name: 'donor_name', label: 'Donor', required: true },
      { name: 'category', label: 'Category', type: 'select', options: ['tithe', 'offering', 'building fund', 'welfare', 'project', 'other'] },
      { name: 'amount', label: 'Amount (₦)', type: 'number', required: true },
      { name: 'method', label: 'Method', type: 'select', options: ['Cash', 'Transfer', 'POS', 'Cheque'] },
      { name: 'note', label: 'Note' },
    ],
    cols: ['donor_name', 'category', 'amount', 'method'],
  },
  'buy-sell/orders': {
    title: 'Orders', singular: 'Order',
    schema: [
      { name: 'listing_title', label: 'Listing', required: true },
      { name: 'buyer_name', label: 'Buyer', required: true },
      { name: 'seller_name', label: 'Seller' },
      { name: 'amount', label: 'Amount (₦)', type: 'number', required: true },
      { name: 'status', label: 'Status', type: 'select', options: ['pending', 'paid', 'shipped', 'delivered', 'disputed', 'completed'] },
    ],
    cols: ['listing_title', 'buyer_name', 'seller_name', 'amount', 'status'],
  },
  'events/tickets': {
    title: 'Tickets', singular: 'Ticket',
    schema: [
      { name: 'event_name', label: 'Event', required: true },
      { name: 'attendee_name', label: 'Attendee', required: true },
      { name: 'tier', label: 'Tier', type: 'select', options: ['Regular', 'VIP', 'VVIP', 'Student'] },
      { name: 'price', label: 'Price (₦)', type: 'number' },
      { name: 'status', label: 'Status', type: 'select', options: ['valid', 'used', 'cancelled'] },
    ],
    cols: ['event_name', 'attendee_name', 'tier', 'price', 'status'],
  },
  'pathway-club/cohorts': {
    title: 'Cohorts', singular: 'Cohort',
    schema: [
      { name: 'name', label: 'Cohort name', required: true },
      { name: 'track', label: 'Track', type: 'select', options: ['ICT Foundation', 'Web Development', 'Data & AI', 'Embedded systems', 'Mobile development'] },
      { name: 'start_date', label: 'Start date', type: 'date' },
      { name: 'end_date', label: 'End date', type: 'date' },
      { name: 'capacity', label: 'Capacity', type: 'number' },
      { name: 'facilitator', label: 'Facilitator' },
    ],
    cols: ['name', 'track', 'start_date', 'end_date', 'capacity'],
  },

  // ============ FINANCIALS & ASSET MANAGEMENT — DETAILED SCHEMAS ============
  'financials/asset-register': {
    title: 'Asset Register', singular: 'Asset',
    schema: [
      { name: 'name', label: 'Asset name', required: true, placeholder: 'Toyota Hilux 2023 / 50" Samsung TV' },
      { name: 'tag_no', label: 'Asset tag #', placeholder: 'AST-0001' },
      { name: 'category', label: 'Category', type: 'select', options: ['Electronics', 'Furniture', 'Vehicles', 'Buildings', 'IT Equipment', 'Machinery', 'Tools', 'Office Equipment', 'Other'] },
      { name: 'manufacturer', label: 'Manufacturer / brand' },
      { name: 'model', label: 'Model / serial #' },
      { name: 'manufacture_date', label: 'Manufacturing date', type: 'date' },
      { name: 'purchase_date', label: 'Purchase date', type: 'date' },
      { name: 'purchase_cost', label: 'Purchase cost (₦)', type: 'number' },
      { name: 'vendor_name', label: 'Vendor / supplier' },
      { name: 'useful_life_years', label: 'Useful life (years)', type: 'number' },
      { name: 'location', label: 'Location', placeholder: 'HQ / Branch / Floor / Room' },
      { name: 'assigned_to', label: 'Assigned user / group' },
      { name: 'department', label: 'Department' },
      { name: 'condition', label: 'Condition', type: 'select', options: ['In use', 'In store', 'Under repair', 'Damaged', 'Disposed', 'Lost'] },
      { name: 'requestor', label: 'Requested by' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
    cols: ['tag_no', 'name', 'category', 'location', 'assigned_to', 'condition', 'purchase_cost'],
  },
  'financials/invoices': {
    title: 'Invoices', singular: 'Invoice',
    schema: [
      { name: 'invoice_no', label: 'Invoice #', placeholder: 'auto' },
      { name: 'client_name', label: 'Bill to (client)', required: true },
      { name: 'client_address', label: 'Client address' },
      { name: 'client_email', label: 'Client email' },
      { name: 'item_description', label: 'Items (one per line)', type: 'textarea', required: true, placeholder: 'Description × Qty × Unit price' },
      { name: 'subtotal', label: 'Subtotal (₦)', type: 'number' },
      { name: 'tax', label: 'Tax / VAT (₦)', type: 'number' },
      { name: 'amount', label: 'Total (₦)', type: 'number', required: true },
      { name: 'issue_date', label: 'Issue date', type: 'date' },
      { name: 'due_date', label: 'Due date', type: 'date' },
      { name: 'status', label: 'Status', type: 'select', options: ['draft', 'sent', 'paid', 'overdue', 'cancelled'] },
      { name: 'notes', label: 'Notes / terms', type: 'textarea' },
    ],
    cols: ['invoice_no', 'client_name', 'amount', 'issue_date', 'due_date', 'status'],
  },
  'financials/vendors': {
    title: 'Vendor Directory', singular: 'Vendor',
    schema: [
      { name: 'name', label: 'Vendor name', required: true },
      { name: 'category', label: 'Category' },
      { name: 'contact_person', label: 'Contact person' },
      { name: 'phone', label: 'Phone' },
      { name: 'email', label: 'Email' },
      { name: 'address', label: 'Address', type: 'textarea' },
      { name: 'tax_id', label: 'Tax ID / TIN' },
      { name: 'lead_time_days', label: 'Lead time (days)', type: 'number' },
      { name: 'rating', label: 'Rating', type: 'select', options: ['', 'A', 'B', 'C', 'D'] },
    ],
    cols: ['name', 'category', 'contact_person', 'phone', 'lead_time_days', 'rating'],
  },
  // re-add the inserted financials registry
  'financials/budget': {
    title: 'Budget & Expenses', singular: 'Budget Item',
    schema: [
      { name: 'name', label: 'Budget line', required: true },
      { name: 'category', label: 'Category' },
      { name: 'period', label: 'Period', placeholder: '2026 Q1' },
      { name: 'budgeted', label: 'Budgeted (₦)', type: 'number', required: true },
      { name: 'spent', label: 'Spent so far (₦)', type: 'number' },
      { name: 'variance', label: 'Variance (₦)', type: 'number' },
      { name: 'status', label: 'Status', type: 'select', options: ['on-track', 'over-budget', 'under-utilised', 'closed'] },
    ],
    cols: ['name', 'category', 'period', 'budgeted', 'spent', 'status'],
  },
  // ============ NEW TOOLS — School ============
  'school/attendance': {
    title: 'Attendance', singular: 'Attendance',
    schema: [
      { name: 'class_arm', label: 'Class & arm', required: true },
      { name: 'date', label: 'Date', type: 'date', required: true },
      { name: 'present', label: 'Present', type: 'number' },
      { name: 'absent', label: 'Absent', type: 'number' },
      { name: 'late', label: 'Late', type: 'number' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
    cols: ['date', 'class_arm', 'present', 'absent', 'late'],
  },
  'school/report-cards': {
    title: 'Report Cards', singular: 'Report Card',
    schema: [
      { name: 'student_name', label: 'Student', required: true },
      { name: 'class_arm', label: 'Class & arm' },
      { name: 'term', label: 'Term', type: 'select', options: ['Term 1', 'Term 2', 'Term 3'] },
      { name: 'session', label: 'Session', placeholder: '2025/2026' },
      { name: 'avg_score', label: 'Average score', type: 'number' },
      { name: 'position', label: 'Position', type: 'number' },
      { name: 'remark', label: 'Remark', type: 'textarea' },
    ],
    cols: ['student_name', 'class_arm', 'term', 'session', 'avg_score', 'position'],
  },
  'school/cbt': {
    title: 'CBT Tests', singular: 'CBT Test',
    schema: [
      { name: 'title', label: 'Test title', required: true },
      { name: 'subject', label: 'Subject' },
      { name: 'class_arm', label: 'Class & arm' },
      { name: 'duration_minutes', label: 'Duration (minutes)', type: 'number' },
      { name: 'questions_count', label: '# Questions', type: 'number' },
      { name: 'status', label: 'Status', type: 'select', options: ['draft', 'scheduled', 'live', 'closed'] },
    ],
    cols: ['title', 'subject', 'class_arm', 'duration_minutes', 'questions_count', 'status'],
  },
  'school/parents': {
    title: 'Parents Portal', singular: 'Parent',
    schema: [
      { name: 'name', label: 'Parent name', required: true },
      { name: 'phone', label: 'Phone' },
      { name: 'email', label: 'Email' },
      { name: 'child_name', label: 'Child name' },
      { name: 'class_arm', label: 'Child class' },
      { name: 'relation', label: 'Relation', type: 'select', options: ['Father', 'Mother', 'Guardian'] },
    ],
    cols: ['name', 'phone', 'child_name', 'class_arm', 'relation'],
  },
  'school/transport': {
    title: 'Transport & ID', singular: 'Route / ID Card',
    schema: [
      { name: 'route_name', label: 'Route name', required: true },
      { name: 'driver', label: 'Driver' },
      { name: 'plate_no', label: 'Plate number' },
      { name: 'pickup_point', label: 'Pickup point' },
      { name: 'time', label: 'Pickup time' },
      { name: 'capacity', label: 'Capacity', type: 'number' },
    ],
    cols: ['route_name', 'driver', 'plate_no', 'pickup_point', 'time', 'capacity'],
  },

  // ============ NEW TOOLS — Estate ============
  'estate/utilities': {
    title: 'Utility Billing', singular: 'Utility Bill',
    schema: [
      { name: 'unit_no', label: 'Unit / plot', required: true },
      { name: 'utility', label: 'Utility', type: 'select', options: ['Electricity', 'Water', 'Gas', 'Refuse'] },
      { name: 'previous_reading', label: 'Previous reading', type: 'number' },
      { name: 'current_reading', label: 'Current reading', type: 'number' },
      { name: 'amount', label: 'Amount (₦)', type: 'number' },
      { name: 'period', label: 'Period', placeholder: 'May 2026' },
      { name: 'status', label: 'Status', type: 'select', options: ['outstanding', 'paid'] },
    ],
    cols: ['unit_no', 'utility', 'current_reading', 'amount', 'period', 'status'],
  },
  'estate/security': {
    title: 'Security & Patrol', singular: 'Patrol Log',
    schema: [
      { name: 'officer', label: 'Officer', required: true },
      { name: 'shift', label: 'Shift', type: 'select', options: ['Day', 'Night', 'Overnight'] },
      { name: 'date', label: 'Date', type: 'date' },
      { name: 'incidents', label: 'Incidents', type: 'textarea' },
      { name: 'severity', label: 'Severity', type: 'select', options: ['Routine', 'Notable', 'Critical'] },
    ],
    cols: ['date', 'officer', 'shift', 'severity'],
  },
  'estate/community': {
    title: 'Community Board', singular: 'Notice',
    schema: [
      { name: 'title', label: 'Title', required: true },
      { name: 'kind', label: 'Type', type: 'select', options: ['Notice', 'Vote', 'Event', 'Announcement'] },
      { name: 'body', label: 'Body', type: 'textarea' },
      { name: 'closes_on', label: 'Closes on', type: 'date' },
    ],
    cols: ['title', 'kind', 'closes_on'],
  },

  // ============ NEW TOOLS — Hospital ============
  'hospital/lab': {
    title: 'Lab Orders', singular: 'Lab Order',
    schema: [
      { name: 'patient_name', label: 'Patient', required: true },
      { name: 'test', label: 'Test', required: true, placeholder: 'FBC / Malaria / RBS' },
      { name: 'priority', label: 'Priority', type: 'select', options: ['routine', 'urgent', 'stat'] },
      { name: 'specimen', label: 'Specimen', type: 'select', options: ['Blood', 'Urine', 'Stool', 'Swab', 'Other'] },
      { name: 'result', label: 'Result', type: 'textarea' },
      { name: 'status', label: 'Status', type: 'select', options: ['ordered', 'collected', 'processing', 'reported'] },
    ],
    cols: ['patient_name', 'test', 'priority', 'status'],
  },
  'hospital/pharmacy': {
    title: 'Pharmacy', singular: 'Drug / Dispense',
    schema: [
      { name: 'drug_name', label: 'Drug name', required: true },
      { name: 'patient_name', label: 'Patient (optional)' },
      { name: 'dose', label: 'Dose / strength' },
      { name: 'quantity', label: 'Quantity', type: 'number' },
      { name: 'price', label: 'Price (₦)', type: 'number' },
      { name: 'status', label: 'Status', type: 'select', options: ['stocked', 'dispensed', 'expired', 'out-of-stock'] },
    ],
    cols: ['drug_name', 'patient_name', 'dose', 'quantity', 'price', 'status'],
  },
  'hospital/ward': {
    title: 'Ward & Beds', singular: 'Admission',
    schema: [
      { name: 'patient_name', label: 'Patient', required: true },
      { name: 'ward', label: 'Ward', type: 'select', options: ['Male Med', 'Female Med', 'Pediatrics', 'Maternity', 'Surgical', 'ICU'] },
      { name: 'bed_no', label: 'Bed #' },
      { name: 'admitting_doctor', label: 'Admitting doctor' },
      { name: 'admit_date', label: 'Admit date', type: 'date' },
      { name: 'status', label: 'Status', type: 'select', options: ['admitted', 'on-leave', 'discharged', 'transferred'] },
    ],
    cols: ['patient_name', 'ward', 'bed_no', 'admitting_doctor', 'admit_date', 'status'],
  },
  'hospital/billing': {
    title: 'Billing & HMO', singular: 'Bill',
    schema: [
      { name: 'patient_name', label: 'Patient', required: true },
      { name: 'service', label: 'Service / item', required: true },
      { name: 'amount', label: 'Amount (₦)', type: 'number', required: true },
      { name: 'payer', label: 'Payer', type: 'select', options: ['Self-pay', 'HMO', 'NHIS', 'Corporate', 'Other'] },
      { name: 'hmo_name', label: 'HMO / scheme name' },
      { name: 'status', label: 'Status', type: 'select', options: ['issued', 'paid', 'pending claim', 'rejected'] },
    ],
    cols: ['patient_name', 'service', 'amount', 'payer', 'status'],
  },
  'hospital/theatre': {
    title: 'Theatre / OT', singular: 'Procedure',
    schema: [
      { name: 'patient_name', label: 'Patient', required: true },
      { name: 'procedure', label: 'Procedure', required: true },
      { name: 'surgeon', label: 'Surgeon' },
      { name: 'theatre_room', label: 'Theatre room' },
      { name: 'scheduled_at', label: 'Scheduled at', type: 'date' },
      { name: 'status', label: 'Status', type: 'select', options: ['scheduled', 'ongoing', 'completed', 'cancelled'] },
    ],
    cols: ['patient_name', 'procedure', 'surgeon', 'theatre_room', 'scheduled_at', 'status'],
  },

  // ============ NEW TOOLS — Engineering ============
  'engineering/dispatch': {
    title: 'Engineer Dispatch', singular: 'Dispatch',
    schema: [
      { name: 'engineer', label: 'Engineer', required: true },
      { name: 'job_ref', label: 'Job reference' },
      { name: 'site', label: 'Site address', required: true },
      { name: 'eta', label: 'ETA' },
      { name: 'gps_lat', label: 'GPS lat' },
      { name: 'gps_lng', label: 'GPS lng' },
      { name: 'status', label: 'Status', type: 'select', options: ['dispatched', 'on-route', 'on-site', 'completed'] },
    ],
    cols: ['engineer', 'job_ref', 'site', 'eta', 'status'],
  },
  'engineering/estimates': {
    title: 'Estimates & Quotes', singular: 'Estimate',
    schema: [
      { name: 'client_name', label: 'Client', required: true },
      { name: 'job_summary', label: 'Job summary', required: true, type: 'textarea' },
      { name: 'labour_cost', label: 'Labour (₦)', type: 'number' },
      { name: 'parts_cost', label: 'Parts (₦)', type: 'number' },
      { name: 'amount', label: 'Total (₦)', type: 'number' },
      { name: 'status', label: 'Status', type: 'select', options: ['draft', 'sent', 'approved', 'declined'] },
    ],
    cols: ['client_name', 'job_summary', 'labour_cost', 'parts_cost', 'amount', 'status'],
  },
  'engineering/sign-off': {
    title: 'Digital Sign-off', singular: 'Sign-off',
    schema: [
      { name: 'job_ref', label: 'Job reference', required: true },
      { name: 'client_name', label: 'Client name' },
      { name: 'engineer', label: 'Engineer' },
      { name: 'signed_at', label: 'Signed at', type: 'date' },
      { name: 'satisfaction', label: 'Client satisfaction', type: 'select', options: ['Excellent', 'Good', 'Fair', 'Poor'] },
      { name: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
    cols: ['job_ref', 'client_name', 'engineer', 'signed_at', 'satisfaction'],
  },
  'engineering/inventory': {
    title: 'Parts Inventory', singular: 'Part',
    schema: [
      { name: 'name', label: 'Part name', required: true },
      { name: 'sku', label: 'SKU' },
      { name: 'category', label: 'Category' },
      { name: 'quantity', label: 'On-hand qty', type: 'number' },
      { name: 'reorder_point', label: 'Reorder point', type: 'number' },
      { name: 'unit_cost', label: 'Unit cost (₦)', type: 'number' },
    ],
    cols: ['name', 'sku', 'category', 'quantity', 'reorder_point', 'unit_cost'],
  },

  // ============ NEW TOOLS — Financials ============
  'financials/reports': {
    title: 'Reports Studio', singular: 'Report',
    schema: [
      { name: 'name', label: 'Report name', required: true },
      { name: 'kind', label: 'Type', type: 'select', options: ['P&L', 'Balance Sheet', 'Cashflow', 'Trial Balance', 'Custom'] },
      { name: 'period_start', label: 'Period start', type: 'date' },
      { name: 'period_end', label: 'Period end', type: 'date' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
    cols: ['name', 'kind', 'period_start', 'period_end'],
  },
  'financials/tax': {
    title: 'Tax Engine', singular: 'Tax Rule',
    schema: [
      { name: 'name', label: 'Rule name', required: true, placeholder: 'NG VAT 7.5%' },
      { name: 'kind', label: 'Type', type: 'select', options: ['VAT', 'WHT', 'PAYE', 'CIT', 'Other'] },
      { name: 'country', label: 'Country', placeholder: 'Nigeria' },
      { name: 'rate', label: 'Rate (%)', type: 'number' },
      { name: 'effective_from', label: 'Effective from', type: 'date' },
    ],
    cols: ['name', 'kind', 'country', 'rate', 'effective_from'],
  },
  'financials/assets': {
    title: 'Asset Register', singular: 'Asset',
    schema: [
      { name: 'name', label: 'Asset name', required: true },
      { name: 'tag_no', label: 'Tag #' },
      { name: 'category', label: 'Category' },
      { name: 'cost', label: 'Cost (₦)', type: 'number' },
      { name: 'acquired_on', label: 'Acquired on', type: 'date' },
      { name: 'depreciation_method', label: 'Depreciation', type: 'select', options: ['Straight-line', 'Reducing balance', 'None'] },
      { name: 'useful_life_years', label: 'Useful life (years)', type: 'number' },
    ],
    cols: ['name', 'tag_no', 'category', 'cost', 'acquired_on'],
  },

  // ============ NEW TOOLS — Office ============
  'office/crm': {
    title: 'CRM', singular: 'Lead',
    schema: [
      { name: 'name', label: 'Contact name', required: true },
      { name: 'company', label: 'Company' },
      { name: 'email', label: 'Email' },
      { name: 'phone', label: 'Phone' },
      { name: 'stage', label: 'Stage', type: 'select', options: ['New', 'Contacted', 'Qualified', 'Proposal', 'Won', 'Lost'] },
      { name: 'value', label: 'Deal value (₦)', type: 'number' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
    cols: ['name', 'company', 'stage', 'value'],
  },
  'office/procurement': {
    title: 'Procurement', singular: 'Purchase Order',
    schema: [
      { name: 'po_no', label: 'PO number' },
      { name: 'vendor', label: 'Vendor', required: true },
      { name: 'item_summary', label: 'Items (summary)', required: true },
      { name: 'amount', label: 'Amount (₦)', type: 'number', required: true },
      { name: 'expected_date', label: 'Expected', type: 'date' },
      { name: 'status', label: 'Status', type: 'select', options: ['draft', 'approved', 'sent', 'received', 'paid', 'cancelled'] },
    ],
    cols: ['po_no', 'vendor', 'item_summary', 'amount', 'status'],
  },
  'office/visitors': {
    title: 'Front Desk', singular: 'Visitor',
    schema: [
      { name: 'visitor_name', label: 'Visitor', required: true },
      { name: 'company', label: 'Company' },
      { name: 'host', label: 'Host', required: true },
      { name: 'purpose', label: 'Purpose' },
      { name: 'arrival_time', label: 'Arrived' },
      { name: 'departure_time', label: 'Departed' },
    ],
    cols: ['visitor_name', 'company', 'host', 'purpose', 'arrival_time'],
  },

  // ============ NEW TOOLS — Restaurant ============
  'restaurant/kitchen': {
    title: 'Kitchen Routing', singular: 'Ticket',
    schema: [
      { name: 'order_no', label: 'Order #', required: true },
      { name: 'station', label: 'Station', type: 'select', options: ['Grill', 'Cold', 'Pastry', 'Fry', 'Bar'] },
      { name: 'items', label: 'Items', type: 'textarea' },
      { name: 'status', label: 'Status', type: 'select', options: ['queued', 'preparing', 'plated', 'served'] },
    ],
    cols: ['order_no', 'station', 'status'],
  },
  'restaurant/tables': {
    title: 'Tables / Floor', singular: 'Table',
    schema: [
      { name: 'table_no', label: 'Table #', required: true },
      { name: 'capacity', label: 'Capacity', type: 'number' },
      { name: 'section', label: 'Section', placeholder: 'Indoor / Patio' },
      { name: 'status', label: 'Status', type: 'select', options: ['available', 'reserved', 'occupied', 'cleaning'] },
      { name: 'guest_name', label: 'Guest (if reserved)' },
    ],
    cols: ['table_no', 'capacity', 'section', 'status', 'guest_name'],
  },
  'restaurant/delivery': {
    title: 'Rider Dispatch', singular: 'Delivery',
    schema: [
      { name: 'order_no', label: 'Order #', required: true },
      { name: 'rider_name', label: 'Rider' },
      { name: 'address', label: 'Drop-off address', required: true },
      { name: 'phone', label: 'Customer phone' },
      { name: 'amount', label: 'Order total (₦)', type: 'number' },
      { name: 'status', label: 'Status', type: 'select', options: ['queued', 'picked-up', 'on-route', 'delivered', 'failed'] },
    ],
    cols: ['order_no', 'rider_name', 'address', 'amount', 'status'],
  },
  'restaurant/pricing': {
    title: 'Time-based Pricing', singular: 'Pricing Rule',
    schema: [
      { name: 'name', label: 'Rule name', required: true },
      { name: 'kind', label: 'Type', type: 'select', options: ['Happy hour', 'Set menu', 'BOGO', 'Discount'] },
      { name: 'starts_at', label: 'Starts (time)' },
      { name: 'ends_at', label: 'Ends (time)' },
      { name: 'discount_pct', label: 'Discount %', type: 'number' },
      { name: 'applies_to', label: 'Applies to (items)', type: 'textarea' },
    ],
    cols: ['name', 'kind', 'starts_at', 'ends_at', 'discount_pct'],
  },

  // ============ NEW TOOLS — Retail ============
  'retail/loyalty': {
    title: 'Loyalty', singular: 'Member',
    schema: [
      { name: 'name', label: 'Customer name', required: true },
      { name: 'phone', label: 'Phone' },
      { name: 'tier', label: 'Tier', type: 'select', options: ['Bronze', 'Silver', 'Gold', 'Platinum'] },
      { name: 'points', label: 'Points', type: 'number' },
      { name: 'lifetime_value', label: 'Lifetime value (₦)', type: 'number' },
    ],
    cols: ['name', 'phone', 'tier', 'points', 'lifetime_value'],
  },
  'retail/promotions': {
    title: 'Promotions', singular: 'Promotion',
    schema: [
      { name: 'name', label: 'Promo name', required: true },
      { name: 'kind', label: 'Type', type: 'select', options: ['BOGO', 'Discount', 'Bundle', 'Free shipping'] },
      { name: 'starts_at', label: 'Starts on', type: 'date' },
      { name: 'ends_at', label: 'Ends on', type: 'date' },
      { name: 'discount_pct', label: 'Discount %', type: 'number' },
      { name: 'description', label: 'Description', type: 'textarea' },
    ],
    cols: ['name', 'kind', 'starts_at', 'ends_at', 'discount_pct'],
  },
  'retail/suppliers': {
    title: 'Suppliers', singular: 'Supplier',
    schema: [
      { name: 'name', label: 'Supplier name', required: true },
      { name: 'contact', label: 'Contact person' },
      { name: 'phone', label: 'Phone' },
      { name: 'email', label: 'Email' },
      { name: 'category', label: 'Category' },
      { name: 'lead_time_days', label: 'Lead time (days)', type: 'number' },
    ],
    cols: ['name', 'contact', 'phone', 'category', 'lead_time_days'],
  },
  'retail/reports': {
    title: 'Z-Reports & EOD', singular: 'Z-Report',
    schema: [
      { name: 'date', label: 'Date', type: 'date', required: true },
      { name: 'cashier', label: 'Cashier' },
      { name: 'gross_sales', label: 'Gross sales (₦)', type: 'number' },
      { name: 'returns', label: 'Returns (₦)', type: 'number' },
      { name: 'net_sales', label: 'Net sales (₦)', type: 'number' },
      { name: 'cash_drop', label: 'Cash drop (₦)', type: 'number' },
    ],
    cols: ['date', 'cashier', 'gross_sales', 'net_sales', 'cash_drop'],
  },

  // ============ NEW TOOLS — Government ============
  'government/services': {
    title: 'Citizen Services', singular: 'Application',
    schema: [
      { name: 'service', label: 'Service', required: true, placeholder: 'Birth certificate, etc.' },
      { name: 'applicant_name', label: 'Applicant', required: true },
      { name: 'phone', label: 'Phone' },
      { name: 'fee', label: 'Fee (₦)', type: 'number' },
      { name: 'status', label: 'Status', type: 'select', options: ['received', 'reviewing', 'approved', 'issued', 'rejected'] },
    ],
    cols: ['service', 'applicant_name', 'fee', 'status'],
  },
  'government/policy': {
    title: 'Policy Chains', singular: 'Approval',
    schema: [
      { name: 'title', label: 'Policy / file title', required: true },
      { name: 'level', label: 'Approval level', type: 'select', options: ['Officer', 'HOD', 'Director', 'Permanent Secretary', 'Commissioner', 'Governor'] },
      { name: 'assignee', label: 'Assignee' },
      { name: 'status', label: 'Status', type: 'select', options: ['pending', 'approved', 'returned', 'rejected'] },
      { name: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
    cols: ['title', 'level', 'assignee', 'status'],
  },
  'government/budget': {
    title: 'Budget & Spend', singular: 'Vote / Release',
    schema: [
      { name: 'vote_code', label: 'Vote code', required: true },
      { name: 'description', label: 'Description', required: true },
      { name: 'budget', label: 'Budget (₦)', type: 'number' },
      { name: 'released', label: 'Released (₦)', type: 'number' },
      { name: 'spent', label: 'Spent (₦)', type: 'number' },
      { name: 'fy', label: 'Fiscal year', placeholder: '2026' },
    ],
    cols: ['vote_code', 'description', 'budget', 'released', 'spent', 'fy'],
  },
  'government/revenue': {
    title: 'Revenue & TIN', singular: 'Collection',
    schema: [
      { name: 'tin', label: 'TIN', required: true },
      { name: 'taxpayer_name', label: 'Taxpayer', required: true },
      { name: 'kind', label: 'Type', type: 'select', options: ['VAT', 'PAYE', 'WHT', 'CIT', 'Levy', 'Fee'] },
      { name: 'amount', label: 'Amount (₦)', type: 'number', required: true },
      { name: 'period', label: 'Period' },
      { name: 'status', label: 'Status', type: 'select', options: ['assessed', 'paid', 'overdue', 'waived'] },
    ],
    cols: ['tin', 'taxpayer_name', 'kind', 'amount', 'period', 'status'],
  },

  // ============ NEW TOOLS — Church / NGO ============
  'church-ngo/pastoral': {
    title: 'Pastoral Care', singular: 'Visit / Follow-up',
    schema: [
      { name: 'member_name', label: 'Member', required: true },
      { name: 'kind', label: 'Type', type: 'select', options: ['Home visit', 'Hospital visit', 'Counselling', 'Phone follow-up'] },
      { name: 'pastor', label: 'Pastor / leader' },
      { name: 'visit_date', label: 'Date', type: 'date' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
    cols: ['member_name', 'kind', 'pastor', 'visit_date'],
  },
  'church-ngo/welfare': {
    title: 'Welfare Cases', singular: 'Case',
    schema: [
      { name: 'beneficiary', label: 'Beneficiary', required: true },
      { name: 'category', label: 'Category', type: 'select', options: ['Medical', 'School fees', 'Bereavement', 'Food', 'Other'] },
      { name: 'amount', label: 'Amount (₦)', type: 'number' },
      { name: 'status', label: 'Status', type: 'select', options: ['open', 'approved', 'disbursed', 'closed'] },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
    cols: ['beneficiary', 'category', 'amount', 'status'],
  },
  'church-ngo/projects': {
    title: 'Projects', singular: 'Project',
    schema: [
      { name: 'name', label: 'Project name', required: true },
      { name: 'goal_amount', label: 'Goal (₦)', type: 'number' },
      { name: 'raised_amount', label: 'Raised (₦)', type: 'number' },
      { name: 'starts_at', label: 'Starts on', type: 'date' },
      { name: 'ends_at', label: 'Ends on', type: 'date' },
      { name: 'status', label: 'Status', type: 'select', options: ['fundraising', 'in-progress', 'completed', 'on-hold'] },
    ],
    cols: ['name', 'goal_amount', 'raised_amount', 'starts_at', 'status'],
  },
  'church-ngo/services': {
    title: 'Services & Events', singular: 'Service',
    schema: [
      { name: 'name', label: 'Service / event name', required: true },
      { name: 'kind', label: 'Type', type: 'select', options: ['Sunday', 'Midweek', 'Crusade', 'Conference', 'Special'] },
      { name: 'date', label: 'Date', type: 'date' },
      { name: 'attendance', label: 'Attendance', type: 'number' },
      { name: 'preacher', label: 'Preacher / speaker' },
      { name: 'theme', label: 'Theme' },
    ],
    cols: ['name', 'kind', 'date', 'attendance', 'preacher'],
  },

  // ============ NEW TOOLS — Buy & Sell ============
  'buy-sell/identity': {
    title: 'Identity Verification', singular: 'KYC Record',
    schema: [
      { name: 'seller_name', label: 'Seller', required: true },
      { name: 'doc_kind', label: 'Document type', type: 'select', options: ['NIN', 'CAC', 'Driver License', 'Passport', 'TIN'] },
      { name: 'doc_number', label: 'Document #' },
      { name: 'status', label: 'Status', type: 'select', options: ['pending', 'verified', 'rejected'] },
      { name: 'verified_at', label: 'Verified on', type: 'date' },
    ],
    cols: ['seller_name', 'doc_kind', 'doc_number', 'status'],
  },
  'buy-sell/logistics': {
    title: 'Delivery Routing', singular: 'Shipment',
    schema: [
      { name: 'order_ref', label: 'Order ref', required: true },
      { name: 'rider_name', label: 'Rider' },
      { name: 'pickup_address', label: 'Pickup' },
      { name: 'dropoff_address', label: 'Dropoff' },
      { name: 'tracking_no', label: 'Tracking #' },
      { name: 'status', label: 'Status', type: 'select', options: ['pending', 'picked-up', 'in-transit', 'delivered', 'failed'] },
    ],
    cols: ['order_ref', 'rider_name', 'tracking_no', 'status'],
  },
  'buy-sell/disputes': {
    title: 'Dispute Resolution', singular: 'Dispute',
    schema: [
      { name: 'order_ref', label: 'Order ref', required: true },
      { name: 'opened_by', label: 'Opened by', type: 'select', options: ['Buyer', 'Seller'] },
      { name: 'reason', label: 'Reason', type: 'textarea' },
      { name: 'evidence', label: 'Evidence (links)', type: 'textarea' },
      { name: 'status', label: 'Status', type: 'select', options: ['open', 'under-review', 'resolved', 'escalated', 'closed'] },
    ],
    cols: ['order_ref', 'opened_by', 'status'],
  },
  'buy-sell/returns': {
    title: 'Returns & Refunds', singular: 'Return',
    schema: [
      { name: 'order_ref', label: 'Order ref', required: true },
      { name: 'reason', label: 'Reason', type: 'select', options: ['Damaged', 'Wrong item', 'Not as described', 'Changed mind', 'Defective'] },
      { name: 'amount', label: 'Refund (₦)', type: 'number' },
      { name: 'step', label: 'Stage', type: 'select', options: ['requested', 'approved', 'pickup', 'received', 'inspected', 'refunded', 'rejected'] },
    ],
    cols: ['order_ref', 'reason', 'amount', 'step'],
  },

  // ============ NEW TOOLS — Events ============
  'events/access': {
    title: 'QR Check-in', singular: 'Check-in',
    schema: [
      { name: 'event_name', label: 'Event', required: true },
      { name: 'attendee_name', label: 'Attendee', required: true },
      { name: 'gate', label: 'Gate', type: 'select', options: ['Main', 'VIP', 'Press', 'Staff'] },
      { name: 'checked_in_at', label: 'Checked-in time' },
      { name: 'status', label: 'Status', type: 'select', options: ['valid', 'denied', 'duplicate'] },
    ],
    cols: ['event_name', 'attendee_name', 'gate', 'status'],
  },
  'events/certs': {
    title: 'Digital Certificates', singular: 'Certificate',
    schema: [
      { name: 'event_name', label: 'Event', required: true },
      { name: 'recipient_name', label: 'Recipient', required: true },
      { name: 'cert_no', label: 'Certificate #' },
      { name: 'issued_at', label: 'Issued', type: 'date' },
      { name: 'qr_code', label: 'QR / verify URL' },
    ],
    cols: ['event_name', 'recipient_name', 'cert_no', 'issued_at'],
  },
  'events/comms': {
    title: 'Mass Invites', singular: 'Campaign',
    schema: [
      { name: 'name', label: 'Campaign name', required: true },
      { name: 'channel', label: 'Channel', type: 'select', options: ['WhatsApp', 'SMS', 'Email'] },
      { name: 'audience', label: 'Audience' },
      { name: 'message', label: 'Message', type: 'textarea' },
      { name: 'status', label: 'Status', type: 'select', options: ['draft', 'scheduled', 'sending', 'sent', 'failed'] },
    ],
    cols: ['name', 'channel', 'audience', 'status'],
  },

  // ============ NEW TOOLS — Pathway Club ============
  'pathway-club/curriculum': {
    title: 'AI Curriculum', singular: 'Path',
    schema: [
      { name: 'track_name', label: 'Track', required: true },
      { name: 'level', label: 'Level', type: 'select', options: ['Beginner', 'Intermediate', 'Advanced'] },
      { name: 'modules_count', label: '# Modules', type: 'number' },
      { name: 'duration_weeks', label: 'Duration (weeks)', type: 'number' },
      { name: 'description', label: 'Description', type: 'textarea' },
    ],
    cols: ['track_name', 'level', 'modules_count', 'duration_weeks'],
  },
  'pathway-club/assessments': {
    title: 'Assessments / CBT', singular: 'Assessment',
    schema: [
      { name: 'title', label: 'Title', required: true },
      { name: 'cohort', label: 'Cohort' },
      { name: 'duration_minutes', label: 'Duration (minutes)', type: 'number' },
      { name: 'pass_mark', label: 'Pass mark (%)', type: 'number' },
      { name: 'status', label: 'Status', type: 'select', options: ['draft', 'live', 'closed'] },
    ],
    cols: ['title', 'cohort', 'duration_minutes', 'pass_mark', 'status'],
  },
  'pathway-club/certs': {
    title: 'Certificates', singular: 'Certificate',
    schema: [
      { name: 'learner_name', label: 'Learner', required: true },
      { name: 'track_name', label: 'Track' },
      { name: 'cert_no', label: 'Certificate #' },
      { name: 'issued_at', label: 'Issued', type: 'date' },
    ],
    cols: ['learner_name', 'track_name', 'cert_no', 'issued_at'],
  },

  // ============ NEW TOOLS — Corporate Hub ============
  'corporate-hub/site': {
    title: 'Brand Site', singular: 'Page',
    schema: [
      { name: 'title', label: 'Page title', required: true },
      { name: 'slug', label: 'URL slug' },
      { name: 'meta_description', label: 'Meta description', type: 'textarea' },
      { name: 'status', label: 'Status', type: 'select', options: ['draft', 'published', 'archived'] },
    ],
    cols: ['title', 'slug', 'status'],
  },
  'corporate-hub/catalog': {
    title: 'Service Catalog', singular: 'Service',
    schema: [
      { name: 'name', label: 'Service name', required: true },
      { name: 'category', label: 'Category' },
      { name: 'price', label: 'Price (₦)', type: 'number' },
      { name: 'duration', label: 'Duration' },
      { name: 'description', label: 'Description', type: 'textarea' },
    ],
    cols: ['name', 'category', 'price', 'duration'],
  },
  'corporate-hub/content': {
    title: 'Content Studio', singular: 'Article',
    schema: [
      { name: 'title', label: 'Title', required: true },
      { name: 'kind', label: 'Type', type: 'select', options: ['Blog', 'Case study', 'Guide', 'Whitepaper'] },
      { name: 'author', label: 'Author' },
      { name: 'status', label: 'Status', type: 'select', options: ['draft', 'in-review', 'published'] },
      { name: 'published_at', label: 'Publish date', type: 'date' },
    ],
    cols: ['title', 'kind', 'author', 'status', 'published_at'],
  },
  'corporate-hub/booking': {
    title: 'Discovery Calls', singular: 'Booking',
    schema: [
      { name: 'name', label: 'Lead name', required: true },
      { name: 'email', label: 'Email' },
      { name: 'company', label: 'Company' },
      { name: 'scheduled_at', label: 'Scheduled', type: 'date' },
      { name: 'duration_minutes', label: 'Duration (min)', type: 'number' },
      { name: 'status', label: 'Status', type: 'select', options: ['booked', 'confirmed', 'completed', 'no-show', 'cancelled'] },
    ],
    cols: ['name', 'company', 'scheduled_at', 'duration_minutes', 'status'],
  },

  // ============ NEW TOOLS — Digital Lab ============
  'digital-laboratory/editor': {
    title: 'Video Editor Projects', singular: 'Project',
    schema: [
      { name: 'name', label: 'Project name', required: true },
      { name: 'duration', label: 'Duration' },
      { name: 'fps', label: 'FPS', type: 'number' },
      { name: 'status', label: 'Status', type: 'select', options: ['drafting', 'editing', 'rendering', 'exported'] },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
    cols: ['name', 'duration', 'fps', 'status'],
  },
  'digital-laboratory/color': {
    title: 'Color Grading', singular: 'Grade',
    schema: [
      { name: 'project_name', label: 'Project', required: true },
      { name: 'lut', label: 'LUT applied' },
      { name: 'reference', label: 'Reference look' },
      { name: 'status', label: 'Status', type: 'select', options: ['pending', 'in-progress', 'approved'] },
    ],
    cols: ['project_name', 'lut', 'status'],
  },
  'digital-laboratory/script': {
    title: 'AI Script Studio', singular: 'Script',
    schema: [
      { name: 'title', label: 'Script title', required: true },
      { name: 'genre', label: 'Genre' },
      { name: 'logline', label: 'Logline', type: 'textarea' },
      { name: 'word_count', label: 'Word count', type: 'number' },
      { name: 'status', label: 'Status', type: 'select', options: ['outline', 'first-draft', 'revised', 'final'] },
    ],
    cols: ['title', 'genre', 'word_count', 'status'],
  },
  'digital-laboratory/image': {
    title: 'Image Studio', singular: 'Image Job',
    schema: [
      { name: 'name', label: 'Job name', required: true },
      { name: 'kind', label: 'Type', type: 'select', options: ['Background remove', 'Upscale', 'Restore', 'Generate'] },
      { name: 'count', label: 'Image count', type: 'number' },
      { name: 'status', label: 'Status', type: 'select', options: ['queued', 'processing', 'completed', 'failed'] },
    ],
    cols: ['name', 'kind', 'count', 'status'],
  },
  'digital-laboratory/audio': {
    title: 'Audio Suite', singular: 'Audio Job',
    schema: [
      { name: 'name', label: 'Job name', required: true },
      { name: 'kind', label: 'Type', type: 'select', options: ['Mix', 'Master', 'Auto-subtitle', 'Voice clean-up', 'Generate VO'] },
      { name: 'duration', label: 'Duration' },
      { name: 'status', label: 'Status', type: 'select', options: ['queued', 'processing', 'completed', 'failed'] },
    ],
    cols: ['name', 'kind', 'duration', 'status'],
  },
};

export default function ModuleSubRecords() {
  const { slug, sub } = useParams();
  const module = moduleBySlug(slug);
  const key = `${slug}/${sub}`;
  let cfg = SUB_REGISTRY[key];
  // Generic fallback schema for any unregistered tool — keeps every tool functional with sensible defaults
  if (!cfg) {
    const title = sub.split('-').map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
    cfg = {
      title,
      singular: title.replace(/s$/, '') || title,
      schema: [
        { name: 'name', label: 'Name / Title', required: true },
        { name: 'category', label: 'Category' },
        { name: 'amount', label: 'Amount (₦)', type: 'number' },
        { name: 'date', label: 'Date', type: 'date' },
        { name: 'reference', label: 'Reference' },
        { name: 'status', label: 'Status', type: 'select', options: ['pending', 'active', 'completed', 'cancelled'] },
        { name: 'notes', label: 'Notes', type: 'textarea' },
      ],
      cols: ['name', 'category', 'amount', 'date', 'status'],
    };
  }
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/deep/${slug}/${sub}/records`, { params: { q: search || undefined } });
      setItems(data.items || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { if (cfg) load(); /* eslint-disable-next-line */ }, [slug, sub, search]);

  if (!module) return <Navigate to="/control-panel" replace />;

  const accent = module.accent;

  const remove = async (rid) => {
    if (!window.confirm('Delete this record?')) return;
    try { await api.delete(`/deep/${slug}/${sub}/records/${rid}`); load(); }
    catch (e) { alert(formatApiError(e)); }
  };

  const runAction = async (a, row) => {
    if (a.confirm && !window.confirm(a.confirm)) return;
    try {
      const url = '/' + a.endpoint.replace('{id}', row.id);
      const { data } = await api.post(url);
      alert(`Done. ${data.issued ? data.issued + ' records created.' : ''}`);
      load();
    } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <DashboardShell module={module} sectionTitle={cfg.title}>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <Link to={`/dashboard/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-navy mb-2 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to {module.short}
          </Link>
          <h1 className="font-display font-bold text-3xl tracking-tight text-navy">{cfg.title}</h1>
          <p className="text-neutral-600 mt-1 text-sm">Manage {cfg.title.toLowerCase()} for this organization.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-medium text-white text-sm transition-all hover:-translate-y-px" style={{ background: accent }} data-testid="add-sub-record">
          <Plus className="w-4 h-4" /> New {cfg.singular}
        </button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center gap-2">
          <Search className="w-4 h-4 text-neutral-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search…`} className="flex-1 outline-none text-sm" />
          <span className="text-xs text-neutral-500">{items.length} record{items.length === 1 ? '' : 's'}</span>
        </div>
        {loading ? (
          <div className="p-12 text-center text-neutral-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center">
            <div className="mx-auto h-12 w-12 rounded-md flex items-center justify-center mb-3" style={{ background: `${accent}10`, color: accent }}>
              <Plus className="w-6 h-6" />
            </div>
            <h4 className="font-display font-semibold text-base text-navy mb-1">No {cfg.title.toLowerCase()} yet</h4>
            <button onClick={() => { setEditing(null); setShowForm(true); }} className="inline-flex items-center gap-2 text-sm font-medium" style={{ color: accent }}>
              <Plus className="w-4 h-4" /> Add {cfg.singular}
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50/50">
                {cfg.cols.map((c) => <th key={c} className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-5 py-3">{c.replace(/_/g, ' ')}</th>)}
                <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-3 py-3">Created</th>
                <th className="px-2 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50/60 transition-colors">
                  {cfg.cols.map((c) => (
                    <td key={c} className="px-5 py-3 text-sm text-navy">{renderCell(r, c)}</td>
                  ))}
                  <td className="px-3 py-3 text-xs text-neutral-500 font-mono">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-2 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      {(cfg.actions || []).map((a) => (
                        <button
                          key={a.key}
                          onClick={() => runAction(a, r)}
                          className="px-3 py-1 text-[11px] font-bold uppercase tracking-wider rounded hover:opacity-90 inline-flex items-center gap-1 transition"
                          style={{ background: accent, color: '#fff' }}
                          data-testid={`action-${a.key}-${r.id}`}
                        >
                          {a.label}
                        </button>
                      ))}
                      <button onClick={() => { setEditing(r); setShowForm(true); }} className="p-1.5 rounded hover:bg-neutral-100 text-neutral-500"><Edit3 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(r.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <RecordForm
          slug={slug} sub={sub} schema={cfg.schema} accent={accent}
          editing={editing} title={cfg.singular}
          onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </DashboardShell>
  );
}

function renderCell(r, c) {
  const v = r[c];
  if (v === undefined || v === null || v === '') return '—';
  if (c === 'amount' || c === 'price' || c === 'estimate') return `₦${Number(v).toLocaleString()}`;
  if (c === 'status') {
    const map = { paid: 'emerald', confirmed: 'emerald', completed: 'emerald', valid: 'emerald', issued: 'blue', sent: 'blue', pending: 'amber', waiting: 'amber', 'in-consult': 'amber', overdue: 'rose', rejected: 'rose', outstanding: 'rose', cancelled: 'neutral', draft: 'neutral' };
    const tone = map[v] || 'neutral';
    return <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-${tone}-50 text-${tone}-700 border border-${tone}-200`}>{v}</span>;
  }
  return String(v);
}

function RecordForm({ slug, sub, schema, accent, editing, title, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const init = {}; schema.forEach((f) => { init[f.name] = editing?.[f.name] ?? ''; }); return init;
  });
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError(''); setBusy(true);
    try {
      const payload = {};
      schema.forEach((f) => {
        let v = form[f.name];
        if (f.type === 'number' && v !== '') v = Number(v);
        if (v === '' || v === undefined || v === null) return;
        payload[f.name] = v;
      });
      if (editing) await api.patch(`/deep/${slug}/${sub}/records/${editing.id}`, payload);
      else await api.post(`/deep/${slug}/${sub}/records`, payload);
      onSaved();
    } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-t-md sm:rounded-md shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="label-tag mb-1" style={{ color: accent }}>{editing ? 'Edit' : 'New'}</div>
            <h3 className="font-display font-semibold text-xl text-navy">{title}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {schema.map((f) => (
            <label key={f.name} className="block">
              <span className="block text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-1.5">
                {f.label}{f.required && <span className="text-rose-500">*</span>}
              </span>
              {f.type === 'textarea' ? (
                <textarea value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} required={f.required} rows={3} className="w-full px-3 py-2 border border-neutral-200 rounded-md outline-none focus:border-navy" />
              ) : f.type === 'select' ? (
                <select value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} required={f.required} className="w-full px-3 py-2 border border-neutral-200 rounded-md outline-none bg-white">
                  <option value="">Select…</option>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={f.type || 'text'} value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} required={f.required} placeholder={f.placeholder} className="w-full px-3 py-2 border border-neutral-200 rounded-md outline-none focus:border-navy" />
              )}
            </label>
          ))}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-md text-sm text-rose-700">
              <AlertCircle className="w-4 h-4 mt-0.5" /><span>{error}</span>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline text-sm">Cancel</button>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-medium text-white text-sm disabled:opacity-60" style={{ background: accent }} data-testid="submit-sub-record">{busy ? 'Saving…' : (editing ? 'Save changes' : 'Create')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export { SUB_REGISTRY };
